import React, { useState, useEffect, useRef } from 'react';

const BatteryCard = ({ children, className = '', reserveCapacity, setReserveCapacity, timeToFailure, setTimeToFailure, isPlaying, onReset, ...props }) => {
  const [visibleCrystals, setVisibleCrystals] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef(null);
  const maxCrystals = 24; // 4 rows × 6 rocks per row for better density
  const preRenderedCrystals = useRef(null);
  
  // Pre-generate all crystal positions on component mount
  const generateAllCrystals = () => {
    if (preRenderedCrystals.current) return preRenderedCrystals.current;
    
    const crystals = [];
    const layerSize = 6; // 6 rocks per row for better density
    
    for (let i = 0; i < maxCrystals; i++) {
      const layer = Math.floor(i / layerSize);
      const positionInLayer = i % layerSize;
      
      crystals.push({
        id: i,
        left: 1 + (positionInLayer * 16) + Math.random() * 4,
        bottom: 1 + (layer * 25) + Math.random() * 5,
        size: Math.random() * 120 + 105,
        rotation: Math.random() * 360,
        delay: (i * 100), // Staggered visibility delay in ms
      });
    }
    
    preRenderedCrystals.current = crystals;
    return crystals;
  };
  
  // Animation control logic - just toggle visibility
  useEffect(() => {
    if (isPlaying && visibleCrystals < maxCrystals) {
      if (!intervalRef.current) {
        setIsAnimating(true);
        intervalRef.current = setInterval(() => {
          setVisibleCrystals(prevVisible => {
            if (prevVisible >= maxCrystals) {
              return prevVisible;
            }
            return prevVisible + 1;
          });
        }, 200); // Show crystal every 200ms for smoother animation
      }
    } else if (!isPlaying || visibleCrystals >= maxCrystals) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsAnimating(false);
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, visibleCrystals, maxCrystals]);
  
  // Update capacity based on visible crystal count
  useEffect(() => {
    if (visibleCrystals > 0) {
      const newCapacity = Math.max(20, 100 - (visibleCrystals / maxCrystals * 80)); // Smooth decrease from 100% to 20%
      const newTimeToFailure = Math.max(1, 24 - (visibleCrystals / maxCrystals * 23)); // Smooth decrease from 24 to 1
      
      if (setReserveCapacity) setReserveCapacity(Math.round(newCapacity));
      if (setTimeToFailure) setTimeToFailure(Math.round(newTimeToFailure));
      
      // Auto-restart when capacity reaches minimum (20%) or max crystals reached
      if (newCapacity <= 20 || visibleCrystals >= maxCrystals) {
        setTimeout(() => {
          // Reset visible crystals count
          setVisibleCrystals(0);
          if (setReserveCapacity) setReserveCapacity(100);
          if (setTimeToFailure) setTimeToFailure(24);
          
          // Clear and restart interval if playing
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setIsAnimating(false);
          }
        }, 1000); // Wait 1 second before restarting
      }
    }
  }, [visibleCrystals, maxCrystals, setReserveCapacity, setTimeToFailure]);
  
  // Handle reset
  useEffect(() => {
    if (onReset) {
      const resetAnimation = () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setVisibleCrystals(0);
        setIsAnimating(false);
        if (setReserveCapacity) setReserveCapacity(100);
        if (setTimeToFailure) setTimeToFailure(24);
      };
      
      // Expose reset function to parent
      if (typeof onReset === 'function') {
        onReset.current = resetAnimation;
      }
    }
  }, [onReset, setReserveCapacity, setTimeToFailure]);
  
  // Reset animation when component unmounts
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setVisibleCrystals(0);
      setIsAnimating(false);
    };
  }, []);
  
  const getDangerLevel = () => {
    if (reserveCapacity >= 60) return 'good';
    if (reserveCapacity >= 35) return 'caution';
    return 'danger';
  };
  
  const getDangerColor = () => {
    const level = getDangerLevel();
    if (level === 'good') return '#10b981'; // green
    if (level === 'caution') return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      style={{
        background: 'linear-gradient(145deg, #1f2937 0%, #111827 100%)',
        border: '4px solid #374151',
        borderRadius: '24px',
        position: 'relative',
      }}
      {...props}
    >
      {/* Battery terminals - positioned on top of card like real battery terminals */}
      <div className="absolute -top-8 left-1/4 w-16 h-12 bg-blue-600 rounded-t-lg border-2 border-blue-500 shadow-lg z-30 flex items-center justify-center">
        <div className="text-white font-bold text-xs">+</div>
      </div>
      <div className="absolute -top-8 right-1/4 w-16 h-12 bg-red-600 rounded-t-lg border-2 border-red-500 shadow-lg z-30 flex items-center justify-center">
        <div className="text-white font-bold text-xs">-</div>
      </div>
      
      {/* Dark background area for crystals */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]" />
      </div>
      
      {/* Sulfation crystals - pre-rendered, visibility toggled */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 2 }}>
        {generateAllCrystals().map((crystal, index) => (
          <div
            key={crystal.id}
            className="absolute"
            style={{
              left: `${crystal.left}%`,
              bottom: `${crystal.bottom}%`,
              width: `${crystal.size}px`,
              height: `${crystal.size}px`,
              transition: 'opacity 0.3s ease-in-out',
              transform: `rotate(${crystal.rotation}deg)`,
              opacity: index < visibleCrystals ? 1 : 0,
              visibility: index < visibleCrystals ? 'visible' : 'hidden',
            }}
          >
            {/* Wrapper div with low opacity for proper transparency */}
            <div style={{ opacity: 0.15 }}>
              <img
                src="/rock-crystal.png"
                alt="Rock crystal"
                width={crystal.size}
                height={crystal.size}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
        ))}
      </div>
      
      {/* Content area with higher z-index */}
      <div className="relative" style={{ zIndex: 10 }}>
        {children}
      </div>

    </div>
  );
};

export default BatteryCard;